// One place that knows which workspace the user is acting in, and what they may
// do in it. Every screen reads from here rather than asking for "the first
// membership", which is what the app did before ADR-0011.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import { supabase } from "./supabase";
import { forgetSignedUrls } from "./api";
import {
  rememberedWorkspaceId, rememberWorkspace, type WorkspaceSummary,
} from "./workspace";
import { type PendingInvitation } from "./people";
import { type Access, UNKNOWN_ACCESS } from "./access";
import { loadSession } from "./session";
import type { ServerProgress } from "../engagement/progress";

interface WorkspaceState {
  workspaces: WorkspaceSummary[];
  active: WorkspaceSummary | null;
  /**
   * Invitations addressed to this account and not yet accepted. Loaded here
   * rather than per screen because the answer is per session, not per page —
   * and because the two questions are the same one: which workspaces am I in,
   * and which have asked me in.
   */
  invitations: PendingInvitation[];
  /**
   * Whether this account is admitted at all (ADR-0023). Loaded here for the
   * same reason as `invitations`: the answer is per session, not per page, and
   * a check the router runs on every navigation must not be a network call.
   */
  access: Access;
  /**
   * The onboarding checklist for the active workspace, as the session answered
   * it. Read once by the engagement module to avoid a second round trip on
   * boot; it owns every refresh after that.
   */
  progress: ServerProgress | null;
  /** null while loading, so screens can tell "none" from "not yet". */
  ready: boolean;
  error: string | null;
  select: (workspaceId: string) => void;
  /** After joining a workspace, or after a role changes. */
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
}

const Ctx = createContext<WorkspaceState | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [access, setAccess] = useState<Access>(UNKNOWN_ACCESS);
  const [activeId, setActiveId] = useState<string | null>(null);
  // The session's own answer, handed to the engagement module so the first
  // checklist costs no extra round trip. Kept WITH the workspace it describes:
  // switching workspace must not hand the new one the old one's checklist, and
  // pairing them is what makes that impossible rather than merely unlikely.
  const [seed, setSeed] = useState<{ id: string; data: ServerProgress } | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    // my_session() is granted to `authenticated` only, so asking for it without
    // a session is not a slow path — it is a hard 42501 that would be shown to a
    // signed-in user later as "permissions could not be loaded".
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setWorkspaces([]);
      setInvitations([]);
      setAccess(UNKNOWN_ACCESS);
      setActiveId(null);
      setSeed(null);
      setError(null);
      setReady(true);
      return;
    }

    // One call, one transaction, one consistent answer (ADR-0027). The gate runs
    // first inside it — which is what provisions a new account — so there is no
    // longer an order here for this client to get wrong, and no way to assemble
    // a snapshot out of reads taken at three different instants.
    //
    // The remembered workspace is sent, not assumed: it is per-browser state,
    // and the server replies with the id it actually used, which is how a
    // remembered id that outlived its membership gets corrected.
    try {
      const snapshot = await loadSession(rememberedWorkspaceId());
      setAccess(snapshot.access);
      setWorkspaces(snapshot.workspaces);
      setActiveId(snapshot.active);
      setInvitations(snapshot.invitations);
      setSeed(snapshot.active && snapshot.progress
        ? { id: snapshot.active, data: snapshot.progress }
        : null);
      if (snapshot.active) rememberWorkspace(snapshot.active);
      setError(null);
    } catch (e: any) {
      setError(e.message ?? String(e));
      // The gate fails OPEN, on purpose: the enforcement is that a waitlisted
      // account owns no workspace and RLS denies it everything, so a failure
      // here can only cost the waitlist screen — while failing closed would
      // lock an admitted user out of a working product over one bad round trip.
      setAccess({ state: "allowed", via: null, waitlistStatus: null, waitlistPosition: null });
    } finally {
      setReady(true);
    }
  }, []);

  // The provider is mounted above the auth gate, so it outlives signing in and
  // signing out. Following the session — rather than loading once on mount —
  // is what stops a signed-out attempt from leaving a stale error on screen for
  // the user who then signs in.
  const loadedFor = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    const loadFor = (userId: string | null) => {
      if (cancelled || loadedFor.current === userId) return; // token refresh: same user
      loadedFor.current = userId;
      // A different person — or nobody — is signing in. Whatever media URLs the
      // last one was holding are theirs, not the next one's (0017-v2 §1).
      forgetSignedUrls();
      setReady(false);
      void load();
    };

    void supabase.auth.getSession().then(({ data }) => loadFor(data.session?.user.id ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange(
      (_e, session) => loadFor(session?.user.id ?? null),
    );
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, [load]);

  const active = useMemo(
    () => workspaces.find((w) => w.workspace_id === activeId) ?? null,
    [workspaces, activeId],
  );

  const select = useCallback((workspaceId: string) => {
    // Signed URLs are bearer authority over one workspace's media and are held
    // in memory for reuse (0017-v2 §1). Switching workspace drops them rather
    // than leaving a live capability for the workspace just left.
    forgetSignedUrls();
    setActiveId(workspaceId);
    rememberWorkspace(workspaceId);
  }, []);

  const value = useMemo<WorkspaceState>(() => ({
    workspaces,
    invitations,
    access,
    // Only ever for the workspace it was read for.
    progress: seed && seed.id === activeId ? seed.data : null,
    active,
    ready,
    error,
    select,
    refresh: load,
    // Enables controls. The server checks again, always.
    can: (permission: string) => !!active?.permissions?.includes(permission),
  }), [workspaces, invitations, access, seed, activeId, active, ready, error, select, load]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWorkspace(): WorkspaceState {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Every signed-in screen renders inside the provider; a missing one is a
    // wiring bug, not a runtime condition to paper over.
    throw new Error("useWorkspace must be used inside a WorkspaceProvider");
  }
  return ctx;
}
